import React, { useState, useMemo } from 'react';
import Modal from '../UI/Modal';
import { FeaturedStrategy } from '../../constants/FeaturedStrategies';
import StrategyService from '../../services/StrategyService';

interface FeaturedStrategiesModalProps {
    isOpen: boolean;
    onClose: () => void;
    strategies: FeaturedStrategy[];
}

const FeaturedStrategiesModal: React.FC<FeaturedStrategiesModalProps> = ({
    isOpen,
    onClose,
    strategies
}) => {
    const [confirmLoad, setConfirmLoad] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const [filters, setFilters] = useState<{
        difficulty: string[];
        cost: string[];
        content: string[];
    }>({
        difficulty: [],
        cost: [],
        content: []
    });

    // Extract unique filter options
    const filterOptions = useMemo(() => {
        const options = {
            difficulty: Array.from(new Set(strategies.map(s => s.difficulty))),
            cost: Array.from(new Set(strategies.map(s => s.cost))),
            content: Array.from(new Set(strategies.flatMap(s => s.content)))
        };
        return options;
    }, [strategies]);

    // Filter and sort strategies
    const filteredStrategies = useMemo(() => {
        let filtered = strategies;

        // Apply filters
        if (filters.difficulty.length || filters.cost.length || filters.content.length) {
            filtered = strategies.filter(strategy => {
                const matchesDifficulty = !filters.difficulty.length || filters.difficulty.includes(strategy.difficulty);
                const matchesCost = !filters.cost.length || filters.cost.includes(strategy.cost);
                const matchesContent = !filters.content.length ||
                    strategy.content.some(tag => filters.content.includes(tag));

                return matchesDifficulty && matchesCost && matchesContent;
            });
        }

        // Sort by version (2.0 first, then 1.0, then no version)
        return filtered.sort((a, b) => {
            const versionA = a.version ?? 0;
            const versionB = b.version ?? 0;
            return versionB - versionA;
        });
    }, [strategies, filters]);

    const handleLoad = (strategy: FeaturedStrategy) => {
        if (confirmLoad === strategy.id) {
            // User has confirmed, load the strategy
            const success = StrategyService.loadStrategy(strategy.shareString, strategy.version);

            if (success) {
                showNotification('Strategy loaded successfully!', 'success');
                // Close the modal after a short delay
                setTimeout(() => {
                    onClose();
                    window.location.reload(); // Reload to ensure all components update
                }, 1000);
            } else {
                showNotification('Failed to load strategy', 'error');
                setConfirmLoad(null);
            }
        } else {
            // Ask for confirmation
            setConfirmLoad(strategy.id);
        }
    };

    // Toggle filter value
    const toggleFilter = (type: 'difficulty' | 'cost' | 'content', value: string) => {
        setFilters(prev => {
            const currentValues = [...prev[type]];

            // If value is already in the filter, remove it
            if (currentValues.includes(value)) {
                return {
                    ...prev,
                    [type]: currentValues.filter(v => v !== value)
                };
            }

            // Otherwise add it
            return {
                ...prev,
                [type]: [...currentValues, value]
            };
        });
    };

    // Clear all filters
    const clearFilters = () => {
        setFilters({
            difficulty: [],
            cost: [],
            content: []
        });
    };

    // Show notification
    const showNotification = (message: string, type: 'success' | 'error') => {
        setNotification({ message, type });

        // Clear notification after 3 seconds
        setTimeout(() => {
            setNotification(null);
        }, 3000);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Featured Strategies" customWidth="max-w-4xl">
            <div className="space-y-4">
                <div className="bg-yellow-800 text-yellow-100 p-3 rounded text-sm mb-4">
                    <p><strong>Warning:</strong> Loading a strategy will overwrite your current setup, including idols and atlas device settings. Save your current setup first to not lose any progress.</p>
                </div>

                {/* Filters */}
                <div className="bg-gray-900 p-3 rounded border border-gray-700">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="font-medium">Filters</h3>
                        <button
                            onClick={clearFilters}
                            className="text-sm text-blue-400 hover:text-blue-300"
                        >
                            Clear All
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Difficulty Filter */}
                        <div>
                            <h4 className="text-sm font-medium mb-2">Difficulty:</h4>
                            <div className="flex flex-wrap gap-2">
                                {filterOptions.difficulty.map(diff => (
                                    <button
                                        key={diff}
                                        onClick={() => toggleFilter('difficulty', diff)}
                                        className={`px-2 py-1 text-xs rounded transition-colors ${filters.difficulty.includes(diff)
                                                ? diff.toLowerCase() === 'easy' ? 'bg-green-600 hover:bg-green-500' :
                                                    diff.toLowerCase() === 'medium' ? 'bg-yellow-600 hover:bg-yellow-500' :
                                                        diff.toLowerCase() === 'hard' ? 'bg-orange-600 hover:bg-orange-500' :
                                                            diff.toLowerCase() === 'very hard' ? 'bg-red-600 hover:bg-red-500' :
                                                                'bg-blue-600 hover:bg-blue-500'
                                                : 'bg-gray-700 hover:bg-gray-600'
                                            }`}
                                    >
                                        {diff}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Cost Filter */}
                        <div>
                            <h4 className="text-sm font-medium mb-2">Cost:</h4>
                            <div className="flex flex-wrap gap-2">
                                {filterOptions.cost.map(cost => (
                                    <button
                                        key={cost}
                                        onClick={() => toggleFilter('cost', cost)}
                                        className={`px-2 py-1 text-xs rounded transition-colors ${filters.cost.includes(cost)
                                                ? cost.toLowerCase() === 'cheap' ? 'bg-green-600 hover:bg-green-500' :
                                                    cost.toLowerCase() === 'medium' ? 'bg-yellow-600 hover:bg-yellow-500' :
                                                        cost.toLowerCase() === 'expensive' ? 'bg-red-600 hover:bg-red-500' :
                                                            'bg-blue-600 hover:bg-blue-500'
                                                : 'bg-gray-700 hover:bg-gray-600'
                                            }`}
                                    >
                                        {cost}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Content Filter */}
                        <div>
                            <h4 className="text-sm font-medium mb-2">Content:</h4>
                            <div className="flex flex-wrap gap-2">
                                {filterOptions.content.map(content => (
                                    <button
                                        key={content}
                                        onClick={() => toggleFilter('content', content)}
                                        className={`px-2 py-1 text-xs rounded transition-colors ${filters.content.includes(content)
                                                ? 'bg-blue-600 hover:bg-blue-500'
                                                : 'bg-gray-700 hover:bg-gray-600'
                                            }`}
                                    >
                                        {content}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Strategy List */}
                {filteredStrategies.length === 0 ? (
                    <p className="text-gray-400 text-center py-4">
                        {strategies.length === 0
                            ? "No featured strategies available."
                            : "No strategies match your selected filters."}
                    </p>
                ) : (
                    <div className="space-y-4 h-[50vh] min-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                        {filteredStrategies.map((strategy) => (
                            <div key={strategy.id} className="bg-gray-700 p-4 rounded">
                                <div className="flex justify-between flex-wrap gap-2 mb-2">
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-semibold text-lg">{strategy.id}</h3>
                                        {strategy.version && (
                                            <div className="relative group">
                                                <span 
                                                    className={`px-2 py-1 text-xs rounded ${
                                                        strategy.version === 1 
                                                            ? 'bg-orange-600' 
                                                            : 'bg-blue-600'
                                                    }`}
                                                >
                                                    {strategy.version.toFixed(1)}
                                                </span>
                                                {strategy.version === 1 && (
                                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg border border-gray-700 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                                        This strategy is from 1.0 Phrecia. The strategy may be outdated.
                                                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                                            <div className="border-4 border-transparent border-t-gray-800"></div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex space-x-2">
                                        <span className={`px-2 py-1 text-xs rounded ${strategy.difficulty.toLowerCase() === 'easy' ? 'bg-green-600' :
                                                strategy.difficulty.toLowerCase() === 'medium' ? 'bg-yellow-600' :
                                                    strategy.difficulty.toLowerCase() === 'hard' ? 'bg-orange-600' :
                                                        strategy.difficulty.toLowerCase() === 'very hard' ? 'bg-red-600' :
                                                            'bg-gray-600'
                                            }`}>
                                            Difficulty: {strategy.difficulty}
                                        </span>
                                        <span className={`px-2 py-1 text-xs rounded ${strategy.cost.toLowerCase() === 'cheap' ? 'bg-green-600' :
                                                strategy.cost.toLowerCase() === 'medium' ? 'bg-yellow-600' :
                                                    strategy.cost.toLowerCase() === 'expensive' ? 'bg-red-600' :
                                                        'bg-gray-600'
                                            }`}>
                                            Cost: {strategy.cost}
                                        </span>
                                    </div>
                                </div>

                                <p className="text-gray-300 mb-3">{strategy.shortDescription}</p>

                                <div className="flex flex-wrap gap-2 mb-3">
                                    {strategy.content.map((tag) => (
                                        <span key={tag} className="bg-blue-900 px-2 py-1 text-xs rounded">
                                            {tag}
                                        </span>
                                    ))}
                                </div>

                                <div className="flex justify-between items-center flex-wrap gap-2">
                                    <div className="flex space-x-4">
                                        {strategy.authorLink && (
                                            <>
                                                <div className="flex items-center">
                                                    {strategy.authorLink.includes('twitch.tv') ? (
                                                        <svg className="w-4 h-4 mr-1 fill-current text-purple-400" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
                                                        </svg>
                                                    ) : (strategy.authorLink.includes('youtube.com') || strategy.authorLink.includes('youtu.be')) ? (
                                                        <svg className="w-4 h-4 mr-1 fill-current text-red-500" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                                        </svg>
                                                    ) : (
                                                        <svg className="w-4 h-4 mr-1 fill-current text-blue-400" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" />
                                                        </svg>
                                                    )}
                                                    <a
                                                        href={strategy.authorLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-400 hover:text-blue-300 text-sm"
                                                    >
                                                        Author
                                                    </a>
                                                </div>
                                                {strategy.strategyLink && <span className="text-gray-500">|</span>}
                                            </>
                                        )}
                                        {strategy.strategyLink && (
                                            <div className="flex items-center">
                                                {strategy.strategyLink.includes('twitch.tv') ? (
                                                    <svg className="w-4 h-4 mr-1 fill-current text-purple-400" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
                                                    </svg>
                                                ) : (strategy.strategyLink.includes('youtube.com') || strategy.strategyLink.includes('youtu.be')) ? (
                                                    <svg className="w-4 h-4 mr-1 fill-current text-red-500" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                                    </svg>
                                                ) : (
                                                    <svg className="w-4 h-4 mr-1 fill-current text-blue-400" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" />
                                                    </svg>
                                                )}
                                                <a
                                                    href={strategy.strategyLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-400 hover:text-blue-300 text-sm"
                                                >
                                                    Strategy Link
                                                </a>
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => handleLoad(strategy)}
                                        className={`px-4 py-1 rounded text-sm ${confirmLoad === strategy.id
                                                ? 'bg-yellow-600 hover:bg-yellow-500'
                                                : 'bg-blue-600 hover:bg-blue-500'
                                            }`}
                                    >
                                        {confirmLoad === strategy.id ? 'Confirm Load' : 'Load Strategy'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Notification */}
            {notification && (
                <div
                    className={`fixed top-4 right-4 px-4 py-2 rounded shadow-lg z-50 ${notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
                        } text-white`}
                >
                    {notification.message}
                </div>
            )}
        </Modal>
    );
};

export default FeaturedStrategiesModal;